import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { Eye, EyeOff, X } from 'lucide-react';
import { BrandMark } from '@/components/brand-mark';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { useLogin } from '@/lib/auth';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const login = useLogin();
  const navigate = useNavigate();

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    try {
      const res = await login.mutateAsync({ email, password });
      if (res.two_factor) {
        navigate('/two-factor-challenge');
      } else {
        navigate('/');
      }
    } catch (err) {
      if (err instanceof AxiosError) {
        const status = err.response?.status;
        // eslint-disable-next-line no-console
        console.error('Login failed', status, err.response?.data, err);
        if (status === 422 || status === 401) {
          setError('Invalid email or password.');
        } else if (status === 419) {
          setError('Session expired — refresh the page and try again.');
        } else if (status === undefined) {
          setError('Could not reach the server (check devtools network tab).');
        } else {
          setError(`Login failed (${status}). See console for details.`);
        }
      } else {
        // eslint-disable-next-line no-console
        console.error('Login error', err);
        setError('Something went wrong. Try again.');
      }
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <BrandMark className="mx-auto mb-4 size-12" />
          <h1 className="text-2xl font-semibold tracking-tight">
            Mehrutherm
          </h1>
          <p className="mt-1 text-sm text-[var(--color-foreground-muted)]">
            Sign in to continue
          </p>
        </div>
        <form className="space-y-4" onSubmit={onSubmit}>
          <label className="block space-y-1.5">
            <span className="text-sm font-medium">Email</span>
            <div className="relative">
              <Input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={email ? 'pr-10' : undefined}
              />
              {email ? (
                <button
                  type="button"
                  onClick={() => setEmail('')}
                  aria-label="Clear email"
                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex size-6 items-center justify-center rounded text-[var(--color-foreground-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-foreground)]"
                >
                  <X className="size-3.5" />
                </button>
              ) : null}
            </div>
          </label>
          <label className="block space-y-1.5">
            <span className="text-sm font-medium">Password</span>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex size-6 items-center justify-center rounded text-[var(--color-foreground-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-foreground)]"
              >
                {showPassword ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
              </button>
            </div>
          </label>
          {error ? (
            <p className="text-sm text-[var(--color-danger)]">{error}</p>
          ) : null}
          <Button type="submit" className="w-full" disabled={login.isPending}>
            {login.isPending ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      </div>
    </div>
  );
}
